// apis/passport.api.ts
import AxiosInstanceCreator from 'services/api';
import {
  ReqCreateSafetySetting,
  ReqCreateSafetyByManual,
  ReqCreateSafetyByNfc,
  ResSafetyDetail,
  ResSafetyMetas,
  ResSafetySettings,
  ReqUpdateSafetySetting,
  ReqSafetyList,
  ResSafetyItem,
  SafetyDetailProps,
  UpdateSafetyStatusProps,
  SafetyAttendanceProps,
  ReqSafetyDetailProps
} from '../model/safety.model';
import { BaseApiResponse, ResultBaseProps } from 'modules/common';
import { AxiosInstance } from 'axios';

const safetyInstance = new AxiosInstanceCreator().create();

export const safetyApi = {
  // 안전 교육 목록 조회 (GET /safety)
  async getSafeties(query: string): Promise<ResSafetyMetas> {
    return safetyInstance.get(`/safety?${query}`).then((res) => res.data);
  },

  // 안전 교육 상세 조회 (GET /safety/{safetyId})
  async getSafetyDetail(safetyId: string, query: string): Promise<ResSafetyDetail> {
    return safetyInstance.get(`/safety/${safetyId}?${query}`).then((res) => res.data);
  },

  // 안전 교육 삭제 (DELETE /safety/edu/{safetyId})
  async deleteSafety(safetyId: string): Promise<ResultBaseProps> {
    return safetyInstance.delete(`/safety/edu/${safetyId}`).then((res) => res.data);
  },

  // 안전 교육 등록 (POST /safety)
  async createSafety(formData: FormData): Promise<ResSafetyDetail> {
    return safetyInstance
      .post('/safety', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => res.data);
  },

  // 안전 교육 수정 (POST /safety/modify)
  async updateSafety(formData: FormData): Promise<ResSafetyDetail> {
    return safetyInstance
      .post('/safety/modify', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => res.data);
  },

  // 안전 교육 상태 변경 (POST /safety/{safety}/{trainer}?status=...)
  async updateSafetyStatus(
    safetyId: string,
    trainerId: string,
    query: string
  ): Promise<ResSafetyDetail> {
    return safetyInstance.post(`/safety/${safetyId}/${trainerId}?${query}`).then((res) => res.data);
  },

  // 안전 교육 참석 정보(단일) 삭제 (DELETE /safety/{safetyAttendance})
  async deleteSafetyAttendance(attendanceId: string): Promise<ResultBaseProps> {
    return safetyInstance.delete(`/safety/${attendanceId}`).then((res) => res.data);
  },

  // 안전 교육 참석 정보 전부 삭제 (DELETE /safety/{safetyId}/attendance)
  async deleteAllSafetyAttendance(safetyId: string): Promise<ResultBaseProps> {
    return safetyInstance.delete(`/safety/${safetyId}/attendance`).then((res) => res.data);
  },

  // 안전 교육 수동 등록 (POST /safety/manual)
  async createSafetyByManual(data: ReqCreateSafetyByManual): Promise<ResultBaseProps> {
    return safetyInstance.post('/safety/manual', data).then((res) => res.data);
  },

  // 안전 교육 nfc 등록 (POST /safety/nfc)
  async createSafetyByNfc(data: ReqCreateSafetyByNfc): Promise<ResultBaseProps> {
    return safetyInstance.post('/safety/nfc', data).then((res) => res.data);
  },

  // 파일 다운로드 (GET /safety/downloads/{fileId})
  // 이건 blob 형태로 받을 수 있으므로 별도 처리 필요 (여기서는 단순 호출 예시)
  async downloadSafetyFile(fileId: number): Promise<Blob> {
    return safetyInstance
      .get(`/safety/downloads/${fileId}`, {
        responseType: 'blob'
      })
      .then((res) => res.data);
  },

  // 시스템 안전교육 세팅 조회
  async fetchSafetySettings(): Promise<ResSafetySettings> {
    return safetyInstance.get(`/system/safety?page=0&size=100`).then((res) => res.data);
  },

  // 시스템 안전교육 세팅 추가
  async createSafetySetting(data: ReqCreateSafetySetting): Promise<ResultBaseProps> {
    return safetyInstance.post('/system/safety', data).then((res) => res.data);
  },

  // 시스템 안전교육 세팅 업데이트
  async updateSafetySetting(data: ReqUpdateSafetySetting): Promise<ResultBaseProps> {
    return safetyInstance.post('/system/safety/modify', data).then((res) => res.data);
  },

  // 시스템 안전교육 세팅 삭제
  async deleteSafetySetting(systemId: string): Promise<ResultBaseProps> {
    return safetyInstance.delete(`/system/safety/${systemId}`).then((res) => res.data);
  }
};

export class SafetyAPI {
  Axios: AxiosInstance;

  constructor() {
    this.Axios = new AxiosInstanceCreator().create();
  }

  /** 안전 교육 목록 조회 API */
  async getSafeties(params: ReqSafetyList): Promise<BaseApiResponse<unknown, ResSafetyItem>> {
    return this.Axios.get<BaseApiResponse<unknown, ResSafetyItem>>('/safety', {
      params
    }).then((res) => res.data);
  }

  /** 안전 교육 상세 조회 API */
  async getSafetyDetail(
    safetyId: string,
    params: Omit<ReqSafetyDetailProps, 'safetyId'>
  ): Promise<BaseApiResponse<SafetyDetailProps, SafetyAttendanceProps>> {
    return await this.Axios.get<BaseApiResponse<SafetyDetailProps, SafetyAttendanceProps>>(
      `/safety/${safetyId}`,
      {
        params
      }
    ).then((res) => res.data);
  }

  /** 안전 교육 등록 API */
  async createSafety(data: FormData): Promise<BaseApiResponse<SafetyDetailProps>> {
    return this.Axios.post<BaseApiResponse<SafetyDetailProps>>('/safety', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then((res) => res.data);
  }

  /** 안전 교육 수정 API */
  async modifySafety(data: FormData): Promise<BaseApiResponse<SafetyDetailProps>> {
    return await this.Axios.post<BaseApiResponse<SafetyDetailProps>>('/safety/modify', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then((res) => res.data);
  }

  /** 안전 교육 상태 업데이트 API */
  async updateSafetyStatus(data: UpdateSafetyStatusProps): Promise<BaseApiResponse> {
    return await this.Axios.put<BaseApiResponse>(`/safety/${data.safety}/status`, {
      status: data.status
    }).then((res) => res.data);
  }

  /** 안전 교육 참석 정보 삭제 API */
  async deleteSafetyAttendance(attendanceId: string): Promise<BaseApiResponse> {
    return await this.Axios.delete<BaseApiResponse>(`/safety/${attendanceId}`).then(
      (res) => res.data
    );
  }

  /** 안전 교육 수동 등록 API */
  async createSafetyByManual(data: ReqCreateSafetyByManual): Promise<BaseApiResponse> {
    return await this.Axios.post<BaseApiResponse>('/safety/manual', data).then((res) => res.data);
  }
}
